/**
 * Resource skill endorsement model — TEZY2-4333 [S10]
 *
 * Promotes the flat account→resource skill tag into an endorsement-based trust
 * signal. A resource skill now carries global endorsement/rejection counts plus
 * the viewing account's own active decision.
 *
 * Contract mirrors the S6 backend read (see Architecture: "Skills & Talent
 * Management — Endorsement & Recording"): the extended
 * `GET /{acct}/resources/{resourceId}/skills` response adds
 * `endorsementCount`, `rejectionCount`, `currentAccountReviewState`,
 * `originType` and `visibilityScope`.
 */

/** The viewing account's own active decision on a resource skill. */
export enum SkillReviewState {
  Endorsed = "endorsed",
  Rejected = "rejected",
  None = "none"
}

/** How a skill came to be on the resource. */
export enum SkillOriginType {
  /** An account assigned the skill on web (assignment == auto-endorsement). */
  AccountAssigned = "account_assigned",
  /** The resource self-added the skill from the mobile Talent Passport. */
  ResourceAdded = "resource_added"
}

/** Visibility of a resource-added skill. */
export enum SkillVisibilityScope {
  Private = "private",
  SharedWithConnections = "shared_with_connections"
}

/**
 * A resource's skill enriched with endorsement signals.
 *
 * `endorsementCount` / `rejectionCount` are GLOBAL signals every account sees.
 * `currentAccountReviewState` is PERSONAL — the viewing account's own decision.
 */
export interface ResourceSkill {
  /** Skill catalogue UUID (stable identity used across the app). */
  uuid: string;
  /**
   * The per-resource skill record id. Needed by S11 endorse/reject/clear
   * actions; optional here because S10 is display-only.
   */
  resourceSkillId?: string;
  name: string;
  description?: string;

  /** Global count of currently-active endorsements. */
  endorsementCount: number;
  /** Global count of currently-active rejections. */
  rejectionCount: number;
  /** The viewing account's own active decision. */
  currentAccountReviewState: SkillReviewState;

  originType?: SkillOriginType;
  visibilityScope?: SkillVisibilityScope;
}

/**
 * Normalise a raw skill record (from the API, fake-db, or a catalogue join)
 * into a partial ResourceSkill. Tolerates camelCase/snake_case and plain-uuid
 * shapes so it survives BE contract drift while S6 is still in flight.
 */
export function normaliseResourceSkill(raw: any): ResourceSkill {
  if (raw == null) {
    return {
      uuid: "",
      name: "",
      endorsementCount: 0,
      rejectionCount: 0,
      currentAccountReviewState: SkillReviewState.None
    };
  }

  // A read may return a bare uuid string for an assignment link.
  if (typeof raw === "string") {
    return {
      uuid: raw,
      name: "",
      endorsementCount: 0,
      rejectionCount: 0,
      currentAccountReviewState: SkillReviewState.None
    };
  }

  const state = raw.currentAccountReviewState ?? raw.current_account_review_state;

  return {
    uuid: raw.uuid ?? raw.skillUuid ?? raw.skill_uuid ?? raw.skillId ?? raw.id ?? "",
    resourceSkillId: raw.resourceSkillId ?? raw.resource_skill_id,
    name: raw.name ?? raw.skillName ?? raw.skill_name ?? "",
    description: raw.description,
    endorsementCount: toCount(raw.endorsementCount ?? raw.endorsement_count),
    rejectionCount: toCount(raw.rejectionCount ?? raw.rejection_count),
    currentAccountReviewState: toReviewState(state),
    originType: raw.originType ?? raw.origin_type,
    visibilityScope: raw.visibilityScope ?? raw.visibility_scope
  };
}

/**
 * Whether a skill should be shown to the viewing account.
 *
 * The S6 read is expected to only return visible skills, but we filter
 * defensively on the client too (AC: "Skills not visible to the account are
 * not displayed"). Account-assigned skills are always visible; resource-added
 * skills are hidden while their visibility is still private.
 */
export function isSkillVisibleToAccount(skill: ResourceSkill): boolean {
  if (skill.originType === SkillOriginType.ResourceAdded) {
    return skill.visibilityScope !== SkillVisibilityScope.Private;
  }
  return true;
}

/**
 * De-duplicate skills by uuid (AC: "Each skill appears only once regardless of
 * origin"), merging counts/state so the surviving record keeps real data.
 */
export function dedupeResourceSkills(skills: ResourceSkill[]): ResourceSkill[] {
  const byUuid = new Map<string, ResourceSkill>();
  for (const skill of skills || []) {
    if (!skill?.uuid) {
      continue;
    }
    const existing = byUuid.get(skill.uuid);
    if (!existing) {
      byUuid.set(skill.uuid, skill);
      continue;
    }
    // Keep the richer record: prefer a defined name and a non-neutral decision.
    byUuid.set(skill.uuid, {
      ...existing,
      name: existing.name || skill.name,
      description: existing.description || skill.description,
      endorsementCount: Math.max(existing.endorsementCount, skill.endorsementCount),
      rejectionCount: Math.max(existing.rejectionCount, skill.rejectionCount),
      currentAccountReviewState:
        existing.currentAccountReviewState !== SkillReviewState.None
          ? existing.currentAccountReviewState
          : skill.currentAccountReviewState,
      originType: existing.originType ?? skill.originType,
      visibilityScope: existing.visibilityScope ?? skill.visibilityScope
    });
  }
  return Array.from(byUuid.values());
}

/**
 * The account actions on a resource skill (TEZY2-4334 [S11]).
 *  - `endorse` / `reject` set the account's own decision;
 *  - `clear` removes it.
 */
export type SkillReviewActionType = "endorse" | "reject" | "clear";

/** Payload emitted when the account acts on a skill. */
export interface SkillReviewActionEvent {
  skill: ResourceSkill;
  action: SkillReviewActionType;
}

/**
 * Whether the account may REJECT this skill (TEZY2-4334 [S11] AC).
 *
 * Reject disputes a self-claim, so it applies ONLY to skills the resource added
 * themselves AND shared with connections (`resource_added` +
 * `shared_with_connections`). Account-assigned skills have no self-claim to
 * dispute and the backend returns 403; those are removed via clear (own
 * decision) or by deleting the assignment. Endorse/clear stay available for any
 * visible skill.
 */
export function canRejectSkill(skill: ResourceSkill): boolean {
  if (!skill || !isSkillVisibleToAccount(skill)) {
    return false;
  }
  return (
    skill.originType === SkillOriginType.ResourceAdded &&
    skill.visibilityScope === SkillVisibilityScope.SharedWithConnections
  );
}

/**
 * Compute the skill after the account applies an action — the optimistic update
 * used so counts and the own-state indicator reflect the change immediately
 * (TEZY2-4334 [S11] AC). Pure: returns a new object, never mutates the input.
 *
 * Only the account's OWN prior decision moves the global counts (switching from
 * endorsed → rejected decrements endorsements and increments rejections, etc.).
 * Re-applying the current decision is a no-op; counts never go negative.
 */
export function applyReviewTransition(
  skill: ResourceSkill,
  action: SkillReviewActionType
): ResourceSkill {
  if (!skill) {
    return skill;
  }

  const current = skill.currentAccountReviewState ?? SkillReviewState.None;
  const target =
    action === "endorse"
      ? SkillReviewState.Endorsed
      : action === "reject"
        ? SkillReviewState.Rejected
        : SkillReviewState.None;

  if (target === current) {
    return skill;
  }

  let endorsementCount = skill.endorsementCount || 0;
  let rejectionCount = skill.rejectionCount || 0;

  // Withdraw the account's previous contribution to the global counts.
  if (current === SkillReviewState.Endorsed) {
    endorsementCount = Math.max(0, endorsementCount - 1);
  } else if (current === SkillReviewState.Rejected) {
    rejectionCount = Math.max(0, rejectionCount - 1);
  }

  // Add the new contribution.
  if (target === SkillReviewState.Endorsed) {
    endorsementCount += 1;
  } else if (target === SkillReviewState.Rejected) {
    rejectionCount += 1;
  }

  return {
    ...skill,
    endorsementCount,
    rejectionCount,
    currentAccountReviewState: target
  };
}

/** A role's required skill, as carried on the shift-role form value (S13). */
export interface RequiredSkillRef {
  uuid?: string;
  name?: string;
}

function toCount(value: any): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function toReviewState(value: any): SkillReviewState {
  switch (value) {
    case SkillReviewState.Endorsed:
    case SkillReviewState.Rejected:
      return value;
    default:
      return SkillReviewState.None;
  }
}
